import {
    DataGridPro,
    GridColDef,
    GridRenderCellParams,
    GridRow,
    GridRowProps,
} from '@mui/x-data-grid-pro'
import React, {memo} from "react";
import {AvantColors} from "../../../../../../styles/AvantColors";
import {IndividualReportLink} from "./IndividualReportLink";
import dayjs from "dayjs";
import {toTitleCase} from "../../../../../../util/StringUtils"
import {TestTakerSearchToolbar} from "./TestTakerSearchToolbar"
import {ProductDTO, TakeRecord, TestTakerProps} from "../TestTakerSearchTypes"
import {MenuItem, TextField, Tooltip} from "@mui/material"
import {ClassReportLink} from "./ClassReportLink"
import {GroupType} from "../../../../../common/group/models/GroupMaster"
import {messageStore} from "../../../../../common/messages/MessageStore";
import ApiService from "../../../../../../services/ApiService"
import {NONE_SELECT} from "../../../../../../util/Constants"

/** Memo for this component prevents it from being re-rendered whenever parent state changes
 *  This component should only re-render when there is new data, not when someone edits a filter field */
export const TestTakerSearchTable = memo(function TakeList({
                                                               tableData,
                                                               loading,
                                                               participationCodes
                                                           }: TestTakerProps) {

    async function updateParticipationCode(params: GridRenderCellParams<TakeRecord>, code: number) {
        try {
            await ApiService.updateParticipationCode(params.row.takeId, code)
            const message = code === NONE_SELECT ?
                `Successfully removed participation code.` :
                `Successfully updated participation code to: ${code}.`
            messageStore.setInfoMessage(message)
            params.row.participationCode = code
        } catch (e) {
            messageStore.setErrorMessage(`There was an error updating participation code: ${code}`)
        }
    }

    const participationCodeColumn: GridColDef<TakeRecord> = {
        field: "participationCode",
        headerName: "Participation Code",
        width: 175,
        renderCell: (params: GridRenderCellParams<TakeRecord>) => {
            return (
                <TextField
                    id={`${params.row.takeId}-participation-code`}
                    data-tst-id={`${params.row.takeId}-participation-code`}
                    select
                    value={params.row.participationCode}
                    sx={{'& fieldset': {border: "none"}}}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateParticipationCode(params, Number(e.target.value))}
                >
                    <MenuItem key={"no-participation-code"} value={NONE_SELECT}>{"None"}</MenuItem>
                    {participationCodes?.map?.(participationCode => (
                        <MenuItem key={participationCode.code}
                                  value={participationCode.code}>{participationCode.code} - {participationCode.reason}</MenuItem>
                    ))}
                </TextField>
            )
        }
    }

    let columns: GridColDef<TakeRecord>[] = [
        {
            field: "individualReport",
            headerName: "Individual Report",
            align: "center",
            headerAlign: "center",
            width: 125,
            renderCell: (params: GridRenderCellParams<TakeRecord>) => {
                return (
                    <IndividualReportLink takeId={params.row.takeId}
                                          productId={params.row.product.productId}
                                          classGroupId={params.row.groupInfo.find(g => g.type === GroupType.CLASS)?.id ?? NaN}
                    />
                )
            }
        },
        {
            field: "classReportLink",
            headerName: "Class Report",
            align: "center",
            headerAlign: "center",
            width: 125,
            renderCell: (params: GridRenderCellParams<TakeRecord>) => {
                const classGroupId = params.row.groupInfo.find(g => g.type === GroupType.CLASS)?.id ?? NaN
                return (
                    <ClassReportLink productId={params.row.product.productId} classGroupId={classGroupId}/>
                )
            }
        },
        {
            field: "product",
            headerName: "Product",
            width: 125,
            valueFormatter: ((product: ProductDTO) => product.productDisplayName)
        },
        {field: "testCode", headerName: "Test Code", width: 125},
        {field: "loginName", headerName: "Login Name", width: 125},
        {field: "firstName", headerName: "First Name", width: 125},
        {field: "lastName", headerName: "Last Name", width: 125},
        {
            field: "startDate", headerName: "Start Date", width: 125, valueFormatter: ((dateString: string | null) => {
                return dateString ? dayjs(dateString).format('YYYY-MM-DD HH:mm:ss') : null
            })
        },
        {
            field: "endDate", headerName: "Finish Date", width: 125, valueFormatter: ((dateString: string | null) => {
                return dateString ? dayjs(dateString).format('YYYY-MM-DD HH:mm:ss') : null
            })
        },
        {
            field: "durationInMinutes",
            headerName: "Duration",
            width: 125,
            valueFormatter: ((duration: number | null) => {
                if (!duration) {
                    return null
                }
                return duration > 1 ? duration + "mins" : duration + "min"
            })
        },
        {field: "language", headerName: "Language"},
        {
            field: "takeState", headerName: "Take State", width: 125, valueFormatter: ((v: string) => {
                return v.split('_').map(s => toTitleCase(s)).join(' ')
            })
        },
        {
            field: "rateable",
            headerName: "Rateable",
            width: 125
        }
    ]

    // Unfortunately this is difficult to assign functionally
    // Assigning mutably for now
    if (participationCodes.length > 0) {
        columns.push(participationCodeColumn)
    }

    function CustomRow(props: GridRowProps) {
        if (!props.row.rateable) {
            return <Tooltip title={"Not Rateable"}>
                <GridRow {...props}>{props.children}</GridRow>
            </Tooltip>
        }
        return <GridRow {...props}>{props.children}</GridRow>
    }

    return (
        <DataGridPro
            rows={tableData}
            columns={columns}
            pageSizeOptions={[25, 50, 100, 250, 500]}
            pagination
            autoHeight
            loading={loading}
            getRowId={(row: TakeRecord) => `${row.testCode}${row.loginName}`}
            getRowClassName={params => {
                if (!params.row.rateable) return "notRateable"
                return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }}
            sx={{
                "& .even": {
                    backgroundColor: AvantColors.REPORT_BACKGROUND_GRAY_DARK,
                },
                "& .odd": {
                    backgroundColor: '#FFFFFF',
                },
                "& .notRateable": {
                    backgroundColor: AvantColors.avantLightRed,
                },
                [`& .MuiDataGrid-row.notRateable:hover,
                  & .MuiDataGrid-row.Mui-selected.notRateable,
                  & .MuiDataGrid-row.Mui-selected.notRateable:hover`]: {
                    backgroundColor: AvantColors.avantPaleRed,
                },
                width: "100%",
            }}
            slots={{toolbar: TestTakerSearchToolbar, row: CustomRow}}
            checkboxSelection={true}
        />
    )
})
